const MULTI_BRIEF_SCHEMA = 'feria_multi_brief_responses_v1';

function cleanString(value, fallback = '') {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback;
}

function firstDefined(...values) {
  return values.find(value => value !== undefined && value !== null && value !== '');
}

export function briefAnswerText(item = {}) {
  return cleanString(firstDefined(item.respuesta, item.answer, item.draft, item.value, item.text_answer), '');
}

export function briefQuestionText(item = {}, index = 0) {
  return cleanString(firstDefined(item.pregunta, item.question, item.text, item.titulo, item.label), `Pregunta ${index + 1}`);
}

export function normalizeBriefAnswers(rows = []) {
  if (!Array.isArray(rows)) return [];
  return rows.map((row, index) => {
    const item = row && typeof row === 'object' ? row : {};
    const respuesta = briefAnswerText(item);
    return {
      ...item,
      question_id: cleanString(firstDefined(item.question_id, item.id, item.key), `q_${index + 1}`),
      pregunta: briefQuestionText(item, index),
      respuesta,
      filled: Boolean(respuesta),
      order: Number(item.order || item.sort_order || index + 1),
    };
  });
}

export function briefResponderLabel(responder = {}) {
  return cleanString(
    firstDefined(
      responder.name,
      responder.responder_name,
      responder.nombre,
      responder.full_name,
      responder.email,
      responder.member_email,
      responder.user_email,
    ),
    'Cliente'
  );
}

export function briefResponderKey(responder = {}) {
  return cleanString(
    firstDefined(
      responder.actor_key,
      responder.participant_key,
      responder.member_id ? `member:${responder.member_id}` : '',
      responder.portal_member_id ? `member:${responder.portal_member_id}` : '',
      responder.user_id ? `user:${responder.user_id}` : '',
      responder.cliente_id ? `client:${responder.cliente_id}` : '',
      responder.email ? `email:${String(responder.email).toLowerCase()}` : '',
      responder.member_email ? `email:${String(responder.member_email).toLowerCase()}` : '',
    ),
    `anonymous:${briefResponderLabel(responder).toLowerCase()}`
  );
}

export function buildBriefSubmission(answers = [], responder = {}) {
  const submittedAt = cleanString(firstDefined(responder.submitted_at, responder.submittedAt), new Date().toISOString());
  const actorKey = briefResponderKey(responder);
  return {
    id: cleanString(firstDefined(responder.submission_id, responder.id), `${actorKey}:${submittedAt}`),
    actor_key: actorKey,
    responder_name: briefResponderLabel(responder),
    responder_email: cleanString(firstDefined(responder.email, responder.member_email, responder.user_email), ''),
    responder_role: cleanString(firstDefined(responder.role, responder.cargo, responder.source), ''),
    portal_member_id: cleanString(firstDefined(responder.portal_member_id, responder.member_id), '') || null,
    user_id: cleanString(responder.user_id, '') || null,
    submitted_at: submittedAt,
    answers: normalizeBriefAnswers(answers),
  };
}

export function normalizeBriefResponsePayload(payload) {
  if (payload && typeof payload === 'object' && !Array.isArray(payload) && payload.schema === MULTI_BRIEF_SCHEMA) {
    const submissions = Array.isArray(payload.submissions)
      ? payload.submissions.map((submission, index) => ({
          ...submission,
          id: cleanString(submission.id, `submission_${index + 1}`),
          actor_key: cleanString(submission.actor_key, `submission_${index + 1}`),
          responder_name: briefResponderLabel(submission),
          responder_email: cleanString(submission.responder_email || submission.email, ''),
          submitted_at: cleanString(submission.submitted_at || submission.created_at, ''),
          answers: normalizeBriefAnswers(submission.answers || submission.respuestas || []),
        }))
      : [];
    return {
      schema: MULTI_BRIEF_SCHEMA,
      latest: normalizeBriefAnswers(payload.latest || payload.items || payload.respuestas || (submissions.length ? submissions[submissions.length - 1].answers : []) || []),
      submissions,
    };
  }

  const legacyRows = normalizeBriefAnswers(Array.isArray(payload) ? payload : []);
  return {
    schema: MULTI_BRIEF_SCHEMA,
    latest: legacyRows,
    submissions: legacyRows.length
      ? [{
          id: 'legacy',
          actor_key: 'legacy',
          responder_name: 'Respuesta principal',
          responder_email: '',
          responder_role: '',
          portal_member_id: null,
          user_id: null,
          submitted_at: '',
          answers: legacyRows,
          legacy: true,
        }]
      : [],
  };
}

export function mergeBriefSubmission(existingPayload, answers = [], responder = {}) {
  const normalized = normalizeBriefResponsePayload(existingPayload);
  const submission = buildBriefSubmission(answers, responder);
  const withoutSameActor = normalized.submissions.filter(item => item.actor_key !== submission.actor_key);
  return {
    schema: MULTI_BRIEF_SCHEMA,
    latest: submission.answers,
    submissions: [...withoutSameActor, submission].sort((a, b) => {
      const aTime = new Date(a.submitted_at || 0).getTime();
      const bTime = new Date(b.submitted_at || 0).getTime();
      return aTime - bTime;
    }),
  };
}

export function flattenLatestBriefAnswers(payload) {
  return normalizeBriefResponsePayload(payload).latest;
}

export function groupBriefAnswersByQuestion(payload) {
  const normalized = normalizeBriefResponsePayload(payload);
  const groups = new Map();

  normalized.submissions.forEach((submission) => {
    submission.answers.forEach((answer, index) => {
      const key = cleanString(answer.question_id, `q_${index + 1}`);
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          questionIndex: groups.size,
          question: briefQuestionText(answer, index),
          answers: [],
        });
      }
      groups.get(key).answers.push({
        answer: briefAnswerText(answer),
        responderName: briefResponderLabel(submission),
        responderEmail: cleanString(submission.responder_email, ''),
        responderRole: cleanString(submission.responder_role, ''),
        submittedAt: cleanString(submission.submitted_at, ''),
        legacy: Boolean(submission.legacy),
      });
    });
  });

  return Array.from(groups.values());
}

export function countBriefResponses(payload) {
  return groupBriefAnswersByQuestion(payload).reduce((total, group) => total + group.answers.length, 0);
}

export { MULTI_BRIEF_SCHEMA };
